/** Bounded transport retry for Sportsnew aggregate/navigation reads. */

import {
  classifySportsnewAggregateResponse,
  sportsnewListRetryDelayMs,
} from './sportsnewListRequestState.js';

const waitForRetry = (delayMs, signal) => new Promise((resolve) => {
  if (signal?.aborted) {
    resolve(false);
    return;
  }
  let settled = false;
  let timer = null;
  function finish(value) {
    if (settled) return;
    settled = true;
    signal?.removeEventListener?.('abort', abortRetry);
    resolve(value);
  }
  function abortRetry() {
    clearTimeout(timer);
    finish(false);
  }
  timer = setTimeout(() => finish(true), delayMs);
  signal?.addEventListener?.('abort', abortRetry, { once: true });
});

export async function requestSportsnewAggregateWithRetry({ request, signal, maxRetries = 1, wait = waitForRetry }) {
  if (typeof request !== 'function') throw new TypeError('request must be a function');
  let retries = 0;
  while (!signal?.aborted) {
    let response;
    try {
      response = await request();
    } catch (error) {
      response = { networkError: true, error };
    }
    if (signal?.aborted) break;
    const classification = classifySportsnewAggregateResponse(response);
    if (classification.success || !classification.retryable || retries >= maxRetries) {
      return { response, classification, attempts: retries + 1, cancelled: false };
    }
    const configuredDelay = sportsnewListRetryDelayMs(response);
    const delayMs = Math.min(Math.max(configuredDelay || 1000, 0), 30000);
    const shouldContinue = await wait(delayMs, signal);
    if (!shouldContinue || signal?.aborted) break;
    retries += 1;
  }
  return {
    response: null,
    classification: { success: false, retryable: false, reason: 'cancelled' },
    attempts: retries,
    cancelled: true,
  };
}
